import { useEffect, useState, Fragment } from "react";
import { Button } from "components/button";
import { Header } from "components/header";
import MyTradeInsCard from "components/my-trade-ins-card";
import { useMyTakebacks, useCreateTakeback } from "api/use-takebacks";
import PayallModal from "components/payall-modal";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence } from "framer-motion";
import ImpressumFooter from "components/footer-impressum";
import LoadingSpinner from "components/loading-spinner";
import { getQueryParams } from "lib/utils";
import BrickEmptyState from "assets/brick_empty_state.svg";
import { EVENT_7 } from "utils/tracking";
import { END_OF_PILOT_25_ARGS } from "consts/feature-config";
import useWhen from "hooks/useWhen";

export const MyTradeIns = () => {
  const [takebacks, setTakebacks] = useState();
  const [previousData, setPreviousData] = useState(null);
  const { data, refetch } = useMyTakebacks();
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const [ready, setReady] = useState(false);
  const [readyCount, setReadyCount] = useState(0);
  // State to update the payment status after Payall modal is completed without needing a page reload / re-fetch
  const [payallCompleted, setPayallCompleted] = useState(false);
  const endOfPilot25 = useWhen(END_OF_PILOT_25_ARGS);

  useCreateTakeback();

  useEffect(() => {
    if (
      data &&
      data.length > 0 &&
      JSON.stringify(data) !== JSON.stringify(previousData)
    ) {
      setPreviousData(data);
      setTakebacks((prevTakebacks) => {
        const uniqueEntries = new Map(
          [...(prevTakebacks || []), ...data].map((item) => [item.number, item])
        );
        const sortedEntries = Array.from(uniqueEntries.values()).sort((a, b) =>
          a.sys_created_on < b.sys_created_on ? 1 : -1
        );
        return sortedEntries;
      });
    }

    if (Array.isArray(data) && !data.length) {
      setTakebacks([]);
      setReady(true);
    }
  }, [data, previousData]);

  useEffect(() => {
    if (takebacks?.length > 0 && readyCount === takebacks?.length) {
      setReady(true);
    }
  }, [readyCount, takebacks?.length]);

  return (
    <div className="min-h-screen relative flex flex-col justify-between">
      <div>
        <Header />
        {!ready && (
          <LoadingSpinner
            className="fixed z-30 left-0 top-[81px] w-screen bg-white bg-opacity-25"
            isLoading
          />
        )}
        <div
          className={`container min-h-[calc(100vh - 81px)] m-auto py-16 px-4 md:px-8 ${!ready ? `opacity-0	` : `opacity-100`}`}
        >
          <div className="flex justify-between items-center w-full">
            {takebacks?.length > 0 && (
              <>
                <h1 className="text-4xl md:text-6xl font-semibold">
                  {t("my_trade_ins.title")}
                </h1>
                {!endOfPilot25 && (
                  <Button
                    href={t("urls.trade_in_weight")}
                    variant="secondary"
                    className=""
                    queryParams={{
                      country: queryParams.country || `null`,
                      state: queryParams.state || `null`,
                    }}
                    tracking={[EVENT_7]}
                  >
                    Start New Trade-In
                  </Button>
                )}
              </>
            )}
          </div>

          {takebacks?.length === 0 && ready && (
            <div className="h-[60svh] flex flex-col items-center justify-center">
              <img
                src={BrickEmptyState}
                alt="No trade-ins"
                className="w-16 mb-4"
              />
              <h1 className="text-4xl md:text-6xl font-semibold mb-1">
                {t("my_trade_ins.title")}
              </h1>
              <p className="text-secondary mb-6 text-xl md:text-2xl">
                You haven't created any trade-ins yet.
              </p>
              {!endOfPilot25 && (
                <Button
                  href={t("urls.trade_in_weight")}
                  queryParams={{
                    country: queryParams.country || `null`,
                    state: queryParams.state || `null`,
                  }}
                >
                  Get started
                </Button>
              )}
            </div>
          )}

          {takebacks?.map((takeback, index) => (
            <Fragment key={takeback.number}>
              <AnimatePresence>
                {modalOpen === takeback.number && (
                  <PayallModal
                    caseId={takeback.number}
                    setModalOpen={setModalOpen}
                    setPayallCompleted={(payallComplete) => {
                      refetch();
                      setPayallCompleted(payallComplete);
                    }}
                  />
                )}
              </AnimatePresence>

              <MyTradeInsCard
                setPayallOpen={() => setModalOpen(takeback.number)}
                payallCompleted={payallCompleted}
                setReadyCount={setReadyCount}
                id={takeback.sys_id}
                index={index}
                queryParams={queryParams}
              />
            </Fragment>
          ))}
        </div>
      </div>
      <ImpressumFooter className="" />
    </div>
  );
};
